import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { StorageKind, StorageSubscribe } from '@revelio/core';

import {
  getAllPreferences,
  savePreferencesForScope,
} from '../api/user-preferences-api';
import { PreferenceScope, type UserPreferences } from '../types';
import { getDefaultPreferences } from '../utils/preference-defaults';
import { filterPreferencesForScope } from '../utils/preference-helpers';
import { getPreferenceSavedSyncKey } from '../utils/preference-operations';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';

export interface UseUserPreferencesReturn {
  preferences: UserPreferences;
  savedPreferences: UserPreferences;
  isInitialLoading: boolean;
  isSyncing: boolean;
  error: Error | null;
  syncWithServer: () => Promise<void>;
}

const getLocalPreferences = (scope: PreferenceScope): UserPreferences => {
  const stored = PreferenceBrowserStorage.get(scope);
  const storedGlobal = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL);
  const defaults = getDefaultPreferences(scope);
  return { ...defaults, ...storedGlobal, ...stored };
};

export function useUserPreferences(
  scope: PreferenceScope
): UseUserPreferencesReturn {
  const queryClient = useQueryClient();
  const preferencesQueryKey = ['user-preferences'];
  const [savedPreferences, setSavedPreferences] = useState<UserPreferences>(
    () => getDefaultPreferences(scope)
  );
  const [localPreferences, setLocalPreferences] = useState<UserPreferences>(
    () => getLocalPreferences(scope)
  );
  const [error, setError] = useState<Error | null>(null);
  const [hasHydratedFromServer, setHasHydratedFromServer] = useState(
    () => queryClient.getQueryData(preferencesQueryKey) !== undefined
  );

  const {
    data: allServerPrefs,
    isLoading: isQueryLoading,
    error: loadError,
  } = useQuery<Record<PreferenceScope, Partial<UserPreferences>>>({
    queryKey: preferencesQueryKey,
    queryFn: () => getAllPreferences(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!allServerPrefs) return;
    // Cache ALL scopes to session storage at once
    // This prevents additional API calls when switching between primary filters
    Object.entries(allServerPrefs).forEach(([scopeKey, prefs]) => {
      const scopeForCache = scopeKey as PreferenceScope;
      const existing = PreferenceBrowserStorage.get(scopeForCache);
      // Merge server -> existing so local session edits always win, while new keys
      // from the server are still hydrated into the cache.
      PreferenceBrowserStorage.set(scopeForCache, {
        ...prefs,
        ...existing,
      } as UserPreferences);
    });
  }, [allServerPrefs]);

  useEffect(() => {
    if (!loadError) return;
    // If the server fetch fails, we still consider the hook "hydrated" from local defaults/storage.
    setHasHydratedFromServer(true);
  }, [loadError]);

  // Local preferences hydration with saved preferences
  useEffect(() => {
    if (!allServerPrefs) return;

    const defaults = getDefaultPreferences(scope);
    const mergedSaved = {
      ...defaults,
      ...allServerPrefs[PreferenceScope.GLOBAL],
      ...allServerPrefs[scope],
    } as UserPreferences;
    setSavedPreferences(mergedSaved);

    const localScopedPrefs = PreferenceBrowserStorage.get(scope);
    const localGlobalPrefs = PreferenceBrowserStorage.get(
      PreferenceScope.GLOBAL
    );
    const mergedLocal = {
      ...mergedSaved,
      ...localGlobalPrefs,
      ...localScopedPrefs,
    } as UserPreferences;

    PreferenceBrowserStorage.set(scope, mergedLocal);
    setLocalPreferences(mergedLocal);
    setHasHydratedFromServer(true);
  }, [allServerPrefs, scope]);

  const { mutateAsync: savePreferenceToServer, isPending: isSyncing } =
    useMutation({
      mutationFn: (prefs: Partial<UserPreferences>) =>
        savePreferencesForScope(scope, prefs),
      onSuccess: async (_result, variables) => {
        setSavedPreferences((prev) => ({ ...prev, ...variables }));
        await queryClient.invalidateQueries({ queryKey: preferencesQueryKey });
      },
      onError: (err: unknown) => {
        setError(err as Error);
      },
    });

  // Sync local changes to server
  const syncWithServer = async () => {
    try {
      setError(null);

      const stored = PreferenceBrowserStorage.get(scope);
      const storedGlobal = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL);
      const merged = { ...storedGlobal, ...stored };
      const relevantForScope = filterPreferencesForScope(scope, merged);
      if (Object.keys(relevantForScope).length > 0) {
        await savePreferenceToServer(relevantForScope);
      } else {
        // Consumers also use this as a "refresh saved defaults" signal.
        // If there is nothing relevant to save for this scope, still refresh server state.
        await queryClient.invalidateQueries({ queryKey: preferencesQueryKey });
      }
    } catch (err) {
      setError(err as Error);
      console.error('Failed to sync preferences to server:', err);
      throw err;
    }
  };

  // Listen for local storage changes on the active scope AND the GLOBAL scope,
  // since effective preferences merge GLOBAL + scoped values.
  useEffect(() => {
    const unsubscribeScoped = PreferenceBrowserStorage.listenForChanges(
      scope,
      () => setLocalPreferences(getLocalPreferences(scope))
    );

    let unsubscribeGlobal: (() => void) | undefined;
    if (scope !== PreferenceScope.GLOBAL) {
      unsubscribeGlobal = PreferenceBrowserStorage.listenForChanges(
        PreferenceScope.GLOBAL,
        () => setLocalPreferences(getLocalPreferences(scope))
      );
    }

    // Listen for save-sync notifications to refresh savedPreferences across instances
    const unsubscribeSaveSync = StorageSubscribe.subscribe(
      StorageKind.Session,
      getPreferenceSavedSyncKey(scope),
      () => {
        // Recalculate from local (server-aligned) state
        setSavedPreferences(getLocalPreferences(scope));
      }
    );

    return () => {
      unsubscribeScoped();
      unsubscribeSaveSync();
      if (unsubscribeGlobal) unsubscribeGlobal();
    };
  }, [scope]);

  return {
    preferences: localPreferences,
    savedPreferences,
    isInitialLoading: isQueryLoading || !hasHydratedFromServer,
    isSyncing,
    error: loadError || error || null,
    syncWithServer,
  };
}
